import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Clock, AlertCircle, RefreshCw, Building2, FileText, Copy, Mail, Ban, Eye } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API, formatPrice, formatDate } from '../utils';
import SEPARequestDetailModal from '../SEPARequestDetailModal';

const STATUS_MAP = {
  awaiting_mandate: { label: 'En attente de signature', cls: 'bg-slate-500/20 text-slate-300' },
  pending_approval: { label: 'À valider', cls: 'bg-yellow-500/20 text-yellow-400' },
  approved: { label: 'Approuvé', cls: 'bg-emerald-500/20 text-emerald-400' },
  rejected: { label: 'Refusé', cls: 'bg-red-500/20 text-red-400' },
};

const SEPARequestsView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_approval');
  const [actionBusy, setActionBusy] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // {id} | null
  const [rejectReason, setRejectReason] = useState('');
  const [detailModalId, setDetailModalId] = useState(null); // request ID for which to open the full details modal

  const fetchItems = async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all'
        ? `${API}/admin/sepa-requests`
        : `${API}/admin/sepa-requests?status=${statusFilter}`;
      const r = await axios.get(url, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      toast.error('Impossible de charger les demandes SEPA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [statusFilter]);

  const approve = async (id) => {
    if (!window.confirm("Confirmer l'approbation ? Le 1er prélèvement (caution + 1er loyer) sera lancé immédiatement.")) return;
    setActionBusy(id);
    try {
      const r = await axios.post(`${API}/admin/sepa-requests/${id}/approve`, {}, { withCredentials: true });
      toast.success(`Approuvé. Souscription Stripe créée : ${r.data.subscription_id}`);
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'approbation');
    } finally {
      setActionBusy(null);
    }
  };

  const submitReject = async () => {
    if (!rejectModal || rejectReason.trim().length < 5) {
      toast.error('Motif obligatoire (5 caractères min)');
      return;
    }
    setActionBusy(rejectModal.id);
    try {
      await axios.post(`${API}/admin/sepa-requests/${rejectModal.id}/reject`,
        { reason: rejectReason.trim() }, { withCredentials: true });
      toast.success('Demande refusée. Email envoyé au client.');
      setRejectModal(null);
      setRejectReason('');
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setActionBusy(null);
    }
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/sepa-sign/${id}`;
    navigator.clipboard.writeText(url);
    toast.success('Lien de signature copié dans le presse-papier');
  };

  const resendEmail = async (id) => {
    setActionBusy(id);
    try {
      await axios.post(`${API}/admin/sepa-requests/${id}/resend-email`, {}, { withCredentials: true });
      toast.success('Email de signature renvoyé au client');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setActionBusy(null);
    }
  };

  const cancelRequest = async (id, name) => {
    if (!window.confirm(`Annuler la demande SEPA de ${name} ? Le lien de signature sera désactivé.`)) return;
    setActionBusy(id);
    try {
      await axios.post(`${API}/admin/sepa-requests/${id}/cancel`, {}, { withCredentials: true });
      toast.success('Demande annulée');
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setActionBusy(null);
    }
  };

  const pendingCount = items.filter(i => i.status === 'pending_approval').length;

  return (
    <div className="space-y-4" data-testid="sepa-requests-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-yellow-400" />
            Demandes SEPA
            {pendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-500 text-black text-xs font-bold" data-testid="sepa-pending-badge">
                {pendingCount} en attente
              </span>
            )}
          </h2>
          <p className="text-sm text-slate-400 mt-1">Approuvez ou refusez les demandes de prélèvement SEPA.</p>
        </div>
        <Button variant="outline" size="sm" className="border-slate-700 text-slate-300" onClick={fetchItems}>
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 border-b border-slate-800">
        {[
          { k: 'pending_approval', label: 'À valider' },
          { k: 'awaiting_mandate', label: 'Mandat à signer' },
          { k: 'approved', label: 'Approuvées' },
          { k: 'rejected', label: 'Refusées' },
          { k: 'all', label: 'Toutes' },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setStatusFilter(t.k)}
            className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${
              statusFilter === t.k
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
            data-testid={`sepa-filter-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Chargement...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Aucune demande dans cette catégorie.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Date</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Client</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Pack</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">IBAN</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-400">1er débit</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-400">/mois</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-slate-400">Statut</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {items.map(r => {
                const st = STATUS_MAP[r.status] || STATUS_MAP.pending_approval;
                const isPending = r.status === 'pending_approval';
                const busy = actionBusy === r.id;
                return (
                  <tr key={r.id} className="hover:bg-slate-800/30" data-testid={`sepa-row-${r.id}`}>
                    <td className="px-4 py-3 text-sm text-slate-300">{formatDate(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-white">{r.customer_name}</div>
                      <div className="text-xs text-slate-500">{r.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-200">{r.pack_name}</td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-300">
                      {r.iban_last4 ? `**** ${r.iban_last4}` : (
                        <span className="text-amber-300/70 not-italic font-sans">
                          {r.status === 'awaiting_mandate' ? 'Non signé' : '****'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-yellow-400 font-semibold">{formatPrice(r.first_charge_amount || 0)}</td>
                    <td className="px-4 py-3 text-right text-sm text-slate-300">{formatPrice(r.monthly_price || 0)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isPending ? (
                        <div className="flex gap-2 justify-center">
                          <button
                            disabled={busy}
                            onClick={() => setDetailModalId(r.id)}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 font-semibold disabled:opacity-40"
                            data-testid={`sepa-view-details-${r.id}`}
                          >
                            <Eye className="w-3.5 h-3.5" /> Voir le dossier
                          </button>
                        </div>
                      ) : r.status === 'rejected' && r.rejection_reason ? (
                        <span className="text-xs text-red-300 italic" title={r.rejection_reason}>
                          {r.rejection_reason.length > 28 ? r.rejection_reason.slice(0, 28) + '…' : r.rejection_reason}
                        </span>
                      ) : r.status === 'awaiting_mandate' ? (
                        <div className="flex gap-1 justify-center flex-wrap">
                          <button
                            disabled={busy}
                            onClick={() => copyLink(r.id)}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded bg-slate-700/40 hover:bg-slate-700/70 text-slate-200 disabled:opacity-40"
                            title="Copier le lien de signature pour l'envoyer manuellement (WhatsApp, SMS...)"
                            data-testid={`sepa-copy-link-${r.id}`}
                          >
                            <Copy className="w-3 h-3" /> Copier
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => resendEmail(r.id)}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 disabled:opacity-40"
                            title="Renvoyer l'email de signature au client"
                            data-testid={`sepa-resend-${r.id}`}
                          >
                            <Mail className="w-3 h-3" /> Renvoyer
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => cancelRequest(r.id, r.customer_name)}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded bg-red-500/15 hover:bg-red-500/25 text-red-400 disabled:opacity-40"
                            title="Annuler la demande SEPA"
                            data-testid={`sepa-cancel-${r.id}`}
                          >
                            <Ban className="w-3 h-3" /> Annuler
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-emerald-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Detail modal (review + approve/reject) */}
      {detailModalId && (
        <SEPARequestDetailModal
          requestId={detailModalId}
          onClose={() => setDetailModalId(null)}
          onChanged={fetchItems}
        />
      )}

      {/* Reject Modal (legacy quick reject without opening detail) */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-slate-900 border border-red-500/40 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="sepa-reject-modal">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <h3 className="text-lg font-bold text-white">Refuser la demande SEPA</h3>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Client : <strong className="text-white">{rejectModal.name}</strong>
              <br />
              Le mandat SEPA sera révoqué et un email automatique avec le motif sera envoyé au client.
            </p>
            <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Motif (visible client)</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Ex: IBAN dans un pays non couvert, vérification d'identité requise…"
              className="w-full p-3 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:border-yellow-400 outline-none"
              data-testid="sepa-reject-reason"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setRejectModal(null)}
                className="flex-1 py-2 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="sepa-reject-cancel"
              >
                Annuler
              </button>
              <button
                onClick={submitReject}
                disabled={rejectReason.trim().length < 5 || actionBusy}
                className="flex-1 py-2 rounded bg-red-600 hover:bg-red-500 text-white font-semibold disabled:opacity-40"
                data-testid="sepa-reject-confirm"
              >
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEPARequestsView;
